import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LockKeyOpen, Check, X } from "@phosphor-icons/react";
import { api, formatApiError, ROLE_LABELS, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-800 ring-amber-200",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  denied: "bg-rose-50 text-rose-700 ring-rose-200",
};

export default function EditRequests() {
  const { user } = useAuth();
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(false);
  const isAdmin = user.role === "super_admin";

  const load = () => api.get("/edit-requests").then((r) => setReqs(r.data));
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(true);
    try {
      await api.post(`/edit-requests/${id}/action`, { action, unlock_hours: 4 });
      toast.success(action === "approve" ? "Approved — the file is unlocked for 4 hours" : "Request declined");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 stagger-in max-w-4xl mx-auto" data-testid="edit-requests-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-slate-900">Edit requests</h1>
        <p className="text-sm text-slate-500 mt-0.5">{isAdmin ? "Approve or decline requests to unlock a file for 4 hours." : "Unlock requests you have sent to the admin."}</p>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04)] divide-y divide-slate-100 overflow-hidden">
        {reqs.length === 0 && <div className="p-12 text-center text-sm text-slate-500" data-testid="empty-edit-requests"><LockKeyOpen size={34} weight="duotone" className="mx-auto mb-2 text-slate-300" />No edit requests</div>}
        {reqs.map((r) => (
          <div key={r.id} className="p-4 flex gap-3" data-testid={`edit-request-${r.id}`}>
            <span className="w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><LockKeyOpen size={17} /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/customers/${r.customer_id}`} className="font-semibold text-slate-900 hover:text-brand-700">{r.customer_name}</Link>
                <span className={`text-[11px] font-semibold capitalize px-2 py-0.5 rounded-full ring-1 ring-inset ${STATUS_COLORS[r.status]}`}>{r.status}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{r.requested_by_name} · {ROLE_LABELS[r.requested_by_role]} · {formatDateTime(r.created_at)}</div>
              <div className="text-sm mt-1.5 text-slate-700 bg-slate-50 rounded-lg px-3 py-2">“{r.reason}”</div>
            </div>
            {isAdmin && r.status === "pending" && (
              <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                <button type="button" data-testid={`approve-request-${r.id}`} disabled={busy} onClick={() => act(r.id, "approve")} aria-label="Approve and unlock for 4 hours" title="Approve · unlock 4h"
                  className="tap w-10 h-10 rounded-full bg-brand-600 text-white hover:bg-brand-700 flex items-center justify-center disabled:opacity-50"><Check size={18} weight="bold" /></button>
                <button type="button" data-testid={`deny-request-${r.id}`} disabled={busy} onClick={() => act(r.id, "deny")} aria-label="Decline" title="Decline"
                  className="tap w-10 h-10 rounded-full border border-slate-200 text-slate-600 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center disabled:opacity-50"><X size={18} weight="bold" /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
